import DefaultArticleCard from "./DefaultArticleCard"
import FeaturedArticleCard from "./FeaturedArticleCard"
import VideoArticleCard from "./VideoArticleCard"

import { Color } from "@constants/V2/color"
import { storyblokLinkToYocoLink } from "@utils/storyblok"
import React from "react"

type ArticlePreviewCardProps = {
  article: Storyblok.ArticleListItem
  textColor: Color
  previewStyle: "default" | "featured" | "video"
}

const getReadTimeText = (readTimeMinutes?: string) => {
  const readTime = Math.floor(Number(readTimeMinutes))

  return Number.isFinite(readTime) && readTime >= 1
    ? `${readTime} min read`
    : undefined
}

const VIDEO_ID_PATTERN = /^[\w-]+$/

/**
 * The shared player splits on "?v=" and throws on any other shape, so a URL is
 * reduced to a canonical one it can parse, or dropped so the poster shows.
 */
const getPlayableVideoUrl = (videoUrl?: string) => {
  if (!videoUrl) {
    return undefined
  }

  let parsedUrl: URL

  try {
    parsedUrl = new URL(videoUrl)
  } catch {
    return undefined
  }

  const host = parsedUrl.hostname.replace(/^www\./, "")
  const [firstSegment, secondSegment] = parsedUrl.pathname
    .split("/")
    .filter(Boolean)

  const toYouTubeUrl = (videoId?: string) =>
    videoId && VIDEO_ID_PATTERN.test(videoId)
      ? `https://www.youtube.com/watch?v=${videoId}`
      : undefined

  if (host === "youtu.be") {
    return toYouTubeUrl(firstSegment)
  }

  if (host === "youtube.com" || host === "m.youtube.com") {
    if (firstSegment === "shorts" || firstSegment === "embed") {
      return toYouTubeUrl(secondSegment)
    }

    return toYouTubeUrl(parsedUrl.searchParams.get("v") ?? undefined)
  }

  if (/\.(mp4|webm)$/i.test(parsedUrl.pathname)) {
    return videoUrl
  }

  if (host === "vimeo.com" || host === "player.vimeo.com") {
    const videoId = firstSegment === "video" ? secondSegment : firstSegment

    return videoId && VIDEO_ID_PATTERN.test(videoId)
      ? `https://vimeo.com/${videoId}`
      : undefined
  }

  return undefined
}

const ArticlePreviewCard = ({
  article,
  textColor,
  previewStyle = "default",
}: ArticlePreviewCardProps) => {
  const { content } = article

  const title = content.title
  const imageUrl = content.image?.filename || ""
  const imageAlt = content.image?.alt || ""
  const category = content.category
  const readTimeText = getReadTimeText(content.readTimeMinutes)
  const articleLink = storyblokLinkToYocoLink(content.articleLink)

  if (previewStyle === "video") {
    return (
      <VideoArticleCard
        title={title}
        coverImageUrl={imageUrl}
        coverImageAlt={imageAlt}
        category={category}
        summary={content.summary}
        readTimeText={readTimeText}
        articleLink={articleLink}
        videoUrl={getPlayableVideoUrl(content.video?.filename)}
        textColor={textColor}
      />
    )
  }

  if (previewStyle === "featured") {
    return (
      <FeaturedArticleCard
        title={title}
        imageUrl={imageUrl}
        imageAlt={imageAlt}
        category={category}
        readTimeText={readTimeText}
        summary={content.summary}
        articleLink={articleLink}
        backgroundColor={content.featuredBackgroundColor || Color.Black}
        iconUrl={content.featuredIcon?.filename}
        iconAlt={content.featuredIcon?.alt || ""}
      />
    )
  }

  return (
    <DefaultArticleCard
      title={title}
      imageUrl={imageUrl}
      imageAlt={imageAlt}
      category={category}
      readTimeText={readTimeText}
      articleLink={articleLink}
      textColor={textColor}
    />
  )
}

export default ArticlePreviewCard
